import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useReducedMotion } from 'framer-motion';
import { api } from '../api';
import { useMe } from '../me';
import { Wordmark } from '../components/Brand';
import { isCnpj, isCpf, maskCnpj, maskCpf } from '../brDocuments';
import '../styles/gate.css';

// Three.js só é baixado aqui (e só em tela larga): o resto do app não paga por ele.
const Constellation = lazy(() => import('../components/Constellation'));

const CHAPTERS = [
  { label: 'Malha de inferência', title: 'Uma rede. Todas as máquinas.', body: 'A Clusterizze conecta GPUs e Macs numa única malha. Você usa os modelos; a malha faz o trabalho.' },
  { label: '01 · Distribuída', title: 'Cada máquina é um ponto de luz.', body: 'Um agente leve transforma qualquer computador em nó: ele aceita tarefas e devolve tokens.' },
  { label: '02 · Roteada', title: 'Sua tarefa encontra o nó certo.', body: 'Compartilhado, prioritário, dedicado ou on-premise. Você escolhe como os seus dados são processados.' },
  { label: '03 · Aberta', title: 'Llama, Qwen, Gemma, Mistral.', body: 'Modelos abertos por uma API simples. Sem servidor para manter e sem contrato mínimo.' },
  { label: '04 · Justa', title: 'Pague só pelo token.', body: 'Saldo em reais e o custo visível em cada resposta, tarefa por tarefa.' },
  { label: 'Clusterizze', title: 'Entre na malha.', body: 'Faça login ao lado para começar a usar.' },
] as const;

export function Gate() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [name, setName] = useState('');
  // cadastro: Pessoal (CPF) ou Enterprise (CNPJ → administrador, em 2 passos); erros por campo, inline
  const [kind, setKind] = useState<'personal' | 'enterprise'>('personal');
  const [step, setStep] = useState<1 | 2>(1);
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [fieldErr, setFieldErr] = useState<Partial<Record<'cpf' | 'cnpj' | 'legalName' | 'email', string>>>({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const { refresh } = useMe();
  const nav = useNavigate();

  // ---- flow de scroll (esquerda) ----
  const reduce = useReducedMotion() ?? false;
  const story = useRef<HTMLDivElement>(null);
  const progress = useRef(0);
  const [active, setActive] = useState(0);
  const [glFail, setGlFail] = useState(false);
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 901px)').matches);
  const fail = useCallback(() => setGlFail(true), []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const onScroll = () => {
    const el = story.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const p = max > 0 ? el.scrollTop / max : 0;
    progress.current = p;
    setActive(Math.min(CHAPTERS.length - 1, Math.round(p * (CHAPTERS.length - 1))));
  };
  const goTo = (i: number) => story.current?.scrollTo({ top: i * story.current.clientHeight, behavior: reduce ? 'auto' : 'smooth' });

  // ---- acesso (direita) ----
  const enterprise = mode === 'register' && kind === 'enterprise';
  const switchMode = (m: 'login' | 'register') => { setMode(m); setErr(''); setFieldErr({}); setStep(1); };
  const switchKind = (k: 'personal' | 'enterprise') => { setKind(k); setErr(''); setFieldErr({}); setStep(1); };

  // passo 1 do Enterprise: confere os dados da empresa antes de pedir o administrador
  const checkCompany = () => {
    const f: typeof fieldErr = {};
    if (!isCnpj(cnpj)) f.cnpj = 'CNPJ inválido';
    if (legalName.trim().length < 2) f.legalName = 'Informe a razão social';
    setFieldErr(f);
    if (!Object.keys(f).length) { setErr(''); setStep(2); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    if (enterprise && step === 1) { checkCompany(); return; }
    if (mode === 'register' && !enterprise && !isCpf(cpf)) { setFieldErr({ cpf: 'CPF inválido' }); return; }   // nenhum POST com CPF inválido
    setFieldErr({});
    setBusy(true);
    try {
      const body = mode === 'login' ? { email, password: pass }
        : enterprise ? { accountType: 'enterprise', company: { cnpj, legalName: legalName.trim(), tradeName: tradeName.trim() || undefined }, admin: { name, email, password: pass } }
        : { accountType: 'personal', email, password: pass, name, cpf };
      await api(`/api/client/${mode}`, { method: 'POST', body: JSON.stringify(body) });
      const me = await refresh();
      nav(me && !me.onboardedAt ? '/boas-vindas' : '/chat', { replace: true });
    } catch (e) {
      const msg = (e as Error).message;
      // erro de um campo: aparece junto dele (e o passo 1 reabre se o problema é da empresa)
      if (/CNPJ/.test(msg)) { setFieldErr({ cnpj: msg }); setStep(1); }
      else if (/CPF/.test(msg)) setFieldErr({ cpf: msg });
      else if (/e-mail/.test(msg)) setFieldErr({ email: msg });
      else setErr(msg);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="gate">
      {/* ===== ESQUERDA: a marca, contada em scroll ===== */}
      {wide && (
        <section className="gate-stage">
          <div className="gate-canvas">
            {!glFail && <Suspense fallback={null}><Constellation progress={progress} reduced={reduce} onFail={fail} /></Suspense>}
            {glFail && <div className="gate-static" />}
          </div>

          <header className="gate-topbar"><Wordmark size="sm" /></header>

          <div className="gate-story" ref={story} onScroll={onScroll} tabIndex={0} role="region" aria-label="Apresentação da Clusterizze">
            {CHAPTERS.map((c, i) => (
              <article key={c.title} className={`chapter ${i === active ? 'in' : ''}`} aria-current={i === active}>
                <div className="chapter-in">
                  <span className="chapter-label">{c.label}</span>
                  <h2>{c.title}</h2>
                  <p>{c.body}</p>
                  {i === 0 && <span className="scroll-hint"><i />role para explorar</span>}
                  {i === CHAPTERS.length - 1 && <span className="to-login" aria-hidden="true">→</span>}
                </div>
              </article>
            ))}
          </div>

          <nav className="gate-rail" aria-label="Capítulos">
            {CHAPTERS.map((c, i) => (
              <button key={c.label} type="button" className={i === active ? 'on' : ''} onClick={() => goTo(i)} aria-label={`Ir para: ${c.title}`} aria-current={i === active} />
            ))}
          </nav>
          <span className="gate-count mono" aria-hidden="true">{String(active + 1).padStart(2, '0')} / {String(CHAPTERS.length).padStart(2, '0')}</span>
        </section>
      )}

      {/* ===== DIREITA: acesso, fixo e sem efeitos ===== */}
      <section className="gate-login">
        {!wide && <div className="gate-login-brand"><Wordmark size="md" tagline /></div>}
        <div className="gate-login-in">
          <span className="gate-kicker">Acesso</span>
          <h1>{mode === 'login' ? 'Bem-vindo de volta.' : 'Crie sua conta.'}</h1>
          <p className="gate-sub">{mode === 'login' ? 'Entre na malha de inferência.' : 'Comece a processar IA agora.'}</p>

          <div className="gate-modes" role="tablist" aria-label="Acesso">
            <button type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => switchMode('login')}>Entrar</button>
            <button type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'on' : ''} onClick={() => switchMode('register')}>Criar conta</button>
          </div>

          {mode === 'register' && (
            <div className="gate-kinds" role="tablist" aria-label="Tipo de conta">
              <button type="button" role="tab" aria-selected={kind === 'personal'} className={kind === 'personal' ? 'on' : ''} onClick={() => switchKind('personal')}>Pessoal</button>
              <button type="button" role="tab" aria-selected={kind === 'enterprise'} className={kind === 'enterprise' ? 'on' : ''} onClick={() => switchKind('enterprise')}>Enterprise</button>
            </div>
          )}

          <form className="gate-form" onSubmit={submit} noValidate={mode === 'register'}>
            {enterprise && step === 1 && (
              <>
                <span className="gate-step">Passo 1 de 2 · Dados da empresa</span>
                <label className="field"><span>CNPJ</span>
                  <input value={cnpj} onChange={(e) => setCnpj(maskCnpj(e.target.value))} inputMode="numeric" placeholder="00.000.000/0000-00" autoComplete="off" aria-invalid={Boolean(fieldErr.cnpj)} />
                  <small className="gate-field-err" role="alert">{fieldErr.cnpj}</small>
                </label>
                <label className="field"><span>Razão social</span>
                  <input value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={120} placeholder="Nome registrado da empresa" autoComplete="organization" aria-invalid={Boolean(fieldErr.legalName)} />
                  <small className="gate-field-err" role="alert">{fieldErr.legalName}</small>
                </label>
                <label className="field"><span>Nome fantasia <em>· opcional</em></span>
                  <input value={tradeName} onChange={(e) => setTradeName(e.target.value)} maxLength={80} placeholder="Como a empresa é conhecida" />
                </label>
                <button type="submit" className="gate-submit">Continuar</button>
              </>
            )}

            {(!enterprise || step === 2) && (
              <>
                {enterprise && (
                  <>
                    <span className="gate-step">Passo 2 de 2 · Administrador responsável</span>
                    <p className="gate-note">A conta da empresa não tem nome de pessoa. O administrador pode ser trocado depois.</p>
                  </>
                )}
                {mode === 'register' && (
                  <label className="field"><span>{enterprise ? 'Nome do administrador' : 'Nome'}</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Como quer ser chamado" autoComplete="name" />
                  </label>
                )}
                <label className="field"><span>E-mail</span>
                  <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" aria-invalid={Boolean(fieldErr.email)} />
                  <small className="gate-field-err" role="alert">{fieldErr.email}</small>
                </label>
                <label className="field"><span>Senha {mode === 'register' && <em>· mínimo 8 caracteres</em>}</span>
                  <input type="password" required autoComplete={mode === 'register' ? 'new-password' : 'current-password'} value={pass} onChange={(e) => setPass(e.target.value)} placeholder="••••••••" />
                </label>
                {mode === 'register' && !enterprise && (
                  <label className="field"><span>CPF</span>
                    <input value={cpf} onChange={(e) => setCpf(maskCpf(e.target.value))} inputMode="numeric" placeholder="000.000.000-00" autoComplete="off" aria-invalid={Boolean(fieldErr.cpf)} />
                    <small className="gate-field-err" role="alert">{fieldErr.cpf}</small>
                  </label>
                )}
                <button type="submit" className="gate-submit" disabled={busy}>{busy ? 'Aguarde…' : mode === 'register' ? 'Criar conta' : 'Entrar'}</button>
                {enterprise && <button type="button" className="gate-back" onClick={() => { setErr(''); setStep(1); }} disabled={busy}>Voltar</button>}
              </>
            )}
            <div className="gate-error" role="alert">{err}</div>
          </form>
        </div>
        <footer className="gate-foot">© 2026 clusterizze · malha de inferência</footer>
      </section>
    </div>
  );
}
